import type { News } from '@maru44/huntre-utils/src/models/news'
import { Box, Button, Paper, Stack, Typography } from '@mui/material'
import type { ReactElement } from 'react'
import { IoArrowForward, IoBatteryFullOutline, IoCheckmarkCircle, IoNotificationsOutline, IoPhonePortraitOutline } from 'react-icons/io5'
import { Link } from 'react-router-dom'
import { availableAreaUrl, deviceCampaignLabel, deviceCampaignPriceNumber, devicePriceNote, devicePriceNumber, serviceName } from 'src/config'
import { useNews } from 'src/hooks/news/useNews'
import { formatTimestampToDate } from 'src/utils/firebase'

const TINT = '#FFF6E5'

export const LP = () => {
  return (
    <Stack spacing={{ xs: 10, md: 14 }}>
      <Hero />
      <NewsSection />
      <MissionSection />
      <FeatureWebApp />
      <FeatureNotification />
      <DeviceSection />
      <StorySection />
      <PressSection />
      <FinalCTA />
    </Stack>
  )
}

const Hero = () => {
  return (
    <Box
      sx={{
        position: 'relative',
        borderRadius: { xs: 3, md: 4 },
        overflow: 'hidden',
        background: `linear-gradient(135deg, ${TINT} 0%, #FFFFFF 60%)`,
        border: '1px solid',
        borderColor: 'divider',
        px: { xs: 3, sm: 5, md: 6 },
        py: { xs: 5, md: 8 },
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={{ xs: 4, md: 6 }}
        sx={{
          alignItems: 'center',
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="overline" sx={{ color: 'primary.dark', display: 'block', mb: 1.5, textTransform: 'none' }}>
            IoT × 罠デバイス管理
          </Typography>
          <Typography
            component="h1"
            sx={{
              fontWeight: 800,
              fontSize: { xs: 30, sm: 38, md: 44 },
              letterSpacing: '-0.02em',
              lineHeight: 1.25,
              color: 'text.primary',
            }}
          >
            見回りの負担を、
            <Box component="span" sx={{ color: 'primary.dark' }}>
              テクノロジー
            </Box>
            で軽減する
          </Typography>
          <Typography sx={{ mt: 2.5, fontSize: { xs: 15, md: 17 }, color: 'text.secondary', lineHeight: 1.8 }}>
            本体{deviceCampaignPriceNumber}円（{deviceCampaignLabel}・定価{devicePriceNumber}
            円）、通信費・月額0円。本当に必要な機能だけを詰め込んだ、シンプルでお手頃な{serviceName}IoT罠管理サービスです。
          </Typography>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            sx={{
              mt: 4,
            }}
          >
            <Button
              component={Link}
              to="/inquiry/register"
              variant="contained"
              size="large"
              endIcon={<IoArrowForward />}
              sx={{ borderRadius: 999, px: 4, py: 1.25, fontWeight: 700, boxShadow: 'none', '&:hover': { boxShadow: 4 } }}
            >
              ご利用申請
            </Button>
            <Button
              component={Link}
              to="/deviceTypes/lte-model-0001"
              variant="outlined"
              size="large"
              sx={{ borderRadius: 999, px: 4, py: 1.25, fontWeight: 700 }}
            >
              デバイスを見る
            </Button>
          </Stack>
        </Box>
        <Box
          sx={{
            flex: 1,
            width: '100%',
            aspectRatio: '4 / 3',
            borderRadius: 3,
            overflow: 'hidden',
            backgroundImage: `url(/device_top.jpg)`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            boxShadow: '0 16px 40px -16px rgba(0,0,0,0.25)',
          }}
        />
      </Stack>
    </Box>
  )
}

const MissionSection = () => {
  return (
    <Box>
      <SectionHeading eyebrow="ABOUT" title="IoT罠とその管理サービス" />
      <Stack
        spacing={3}
        sx={{
          mt: 4,
        }}
      >
        <Typography sx={{ fontSize: { xs: 15, md: 17 }, lineHeight: 1.9, color: 'text.secondary' }}>
          {serviceName}は、IoT罠デバイスとその管理Webアプリを提供するサービスです。罠の見回りの負担を大幅に軽減し、使いやすさと価格の両立を実現しました。
        </Typography>
        <Typography sx={{ fontSize: { xs: 15, md: 17 }, lineHeight: 1.9, color: 'text.secondary' }}>
          本サービスは、弊社代表自身が新米ハンターとして「既存のIoT罠は高すぎる」と感じたことから始まりました。本当に必要な最低限の機能だけを実装することで、価格を抑えながら現場で使えるツールを目指しています。
        </Typography>
      </Stack>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        sx={{
          mt: 5,
        }}
      >
        <StatCard
          label="本体価格 (税込)"
          value={`¥${deviceCampaignPriceNumber}`}
          suffix={
            <>
              {deviceCampaignLabel}価格{' '}
              <Box component="span" sx={{ textDecoration: 'line-through' }}>
                定価¥{devicePriceNumber}
              </Box>
            </>
          }
        />
        <StatCard label="通信費" value="¥0" suffix="月額0円・本体価格に含む" />
        <StatCard label="親機・中継機" value="不要" suffix="単独で動作" />
      </Stack>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 2 }}>
        {devicePriceNote}
      </Typography>
    </Box>
  )
}

type StatCardProps = { label: string; value: string; suffix?: React.ReactNode }
const StatCard = ({ label, value, suffix }: StatCardProps) => {
  return (
    <Paper
      elevation={0}
      sx={{
        flex: 1,
        p: 3,
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        backgroundColor: TINT,
      }}
    >
      <Typography variant="overline" sx={{ color: 'text.secondary' }}>
        {label}
      </Typography>
      <Typography sx={{ fontWeight: 800, fontSize: { xs: 28, md: 32 }, color: 'primary.dark', mt: 0.5, lineHeight: 1.1 }}>{value}</Typography>
      {suffix && (
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1 }}>
          {suffix}
        </Typography>
      )}
    </Paper>
  )
}

const FeatureWebApp = () => {
  return (
    <FeatureBlock
      eyebrow="FEATURE 01"
      icon={<IoPhonePortraitOutline />}
      title="Webアプリで一元管理"
      bullets={['Webアプリは無料でご利用いただけます', 'デバイスの購入から一元管理まで Web で完結', 'グループでのデバイス共有も対応。個人から自治体まで']}
      image="/device_list.png"
      imageCaption="※画像は開発時のものです"
      imageAlign="right"
    />
  )
}

const FeatureNotification = () => {
  return (
    <FeatureBlock
      eyebrow="FEATURE 02"
      icon={<IoNotificationsOutline />}
      title="必要な通知だけを、確実に"
      bullets={['捕獲通知に加え、故障や電池切れも検知して通知', 'メール / 管理画面 / LINE に対応', 'ご要望に応じて SMS など今後拡張予定']}
      image="/notification_image.png"
      imageAlign="left"
    />
  )
}

type FeatureBlockProps = {
  eyebrow: string
  icon: ReactElement
  title: string
  bullets: string[]
  image: string
  imageCaption?: string
  imageAlign: 'left' | 'right'
}
const FeatureBlock = ({ eyebrow, icon, title, bullets, image, imageCaption, imageAlign }: FeatureBlockProps) => {
  const text = (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="overline" sx={{ color: 'primary.dark', display: 'block', mb: 1 }}>
        {eyebrow}
      </Typography>
      <Stack
        direction="row"
        spacing={1.5}
        sx={{
          alignItems: 'center',
        }}
      >
        <Box sx={{ display: 'flex', fontSize: 28, color: 'primary.dark' }}>{icon}</Box>
        <Typography variant="h5" sx={{ fontSize: { xs: 22, md: 26 } }}>
          {title}
        </Typography>
      </Stack>
      <Stack
        spacing={1.5}
        sx={{
          mt: 3,
        }}
      >
        {bullets.map((b) => (
          <Stack
            key={b}
            direction="row"
            spacing={1.5}
            sx={{
              alignItems: 'flex-start',
            }}
          >
            <Box sx={{ display: 'flex', color: 'primary.main', fontSize: 20, mt: '2px' }}>
              <IoCheckmarkCircle />
            </Box>
            <Typography sx={{ fontSize: { xs: 15, md: 16 }, lineHeight: 1.7, color: 'text.primary' }}>{b}</Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  )
  const visual = (
    <Box sx={{ flex: 1, width: '100%', minWidth: 0 }}>
      <Paper elevation={0} sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid', borderColor: 'divider', backgroundColor: '#FAFAFA' }}>
        <Box sx={{ p: 1.5, display: 'flex' }}>
          <Box component="img" src={image} alt={title} sx={{ width: '100%', height: 'auto', display: 'block', borderRadius: 2 }} />
        </Box>
      </Paper>
      {imageCaption && (
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1 }}>
          {imageCaption}
        </Typography>
      )}
    </Box>
  )
  const mdDirection = imageAlign === 'right' ? 'row' : 'row-reverse'
  return (
    <Stack
      direction={{ xs: 'column', md: mdDirection }}
      spacing={{ xs: 4, md: 6 }}
      sx={{
        alignItems: 'center',
      }}
    >
      {text}
      {visual}
    </Stack>
  )
}

const DeviceSection = () => {
  return (
    <Box>
      <SectionHeading eyebrow="DEVICE" title="デバイス" />
      <Paper
        elevation={0}
        sx={{
          mt: 4,
          borderRadius: 4,
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          sx={{
            alignItems: 'stretch',
          }}
        >
          <Box
            sx={{
              flex: 1,
              minHeight: { xs: 240, md: 'auto' },
              backgroundColor: TINT,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: { xs: 3, md: 5 },
            }}
          >
            <Box component="img" src="/wanapp_device_image.png" alt="LTEモデル" sx={{ maxWidth: '100%', maxHeight: 280, objectFit: 'contain' }} />
          </Box>
          <Box sx={{ flex: 1, p: { xs: 3, md: 5 } }}>
            <Typography variant="overline" sx={{ color: 'primary.dark' }}>
              CURRENT MODEL
            </Typography>
            <Typography variant="h5" sx={{ mt: 0.5 }}>
              LTEモデル
            </Typography>
            <Stack
              spacing={2.5}
              sx={{
                mt: 3,
              }}
            >
              <SpecRow
                label="料金"
                value={
                  <>
                    <Box component="span" sx={{ color: 'text.secondary', textDecoration: 'line-through', mr: 1 }}>
                      定価{devicePriceNumber}円
                    </Box>
                    <Box component="span" sx={{ fontWeight: 700 }}>
                      {deviceCampaignPriceNumber}円
                    </Box>
                    （税込）
                    <Box
                      component="span"
                      sx={{
                        ml: 1,
                        px: 1,
                        py: 0.25,
                        borderRadius: 999,
                        backgroundColor: 'primary.main',
                        color: '#fff',
                        fontSize: 11,
                        fontWeight: 700,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {deviceCampaignLabel}
                    </Box>
                  </>
                }
              />
              <SpecRow label="通信費" value="無料 (本体価格に含む)" />
              <SpecRow
                label="通信方法"
                value={
                  <>
                    LTE-M ・ 利用可能エリアは
                    <Link to={availableAreaUrl} target="_blank" style={{ color: 'inherit' }}>
                      <Box component="span" sx={{ color: 'primary.dark' }}>
                        こちら
                      </Box>
                    </Link>
                  </>
                }
              />
              <SpecRow label="親機・中継機" value="不要 (単独で動作)" />
            </Stack>
            <Box
              sx={{
                mt: 4,
              }}
            >
              <Button
                component={Link}
                to="/deviceTypes/lte-model-0001"
                variant="text"
                endIcon={<IoArrowForward />}
                sx={{ fontWeight: 700, color: 'primary.dark', '&:hover': { backgroundColor: TINT } }}
              >
                詳細を見る
              </Button>
            </Box>
          </Box>
        </Stack>
      </Paper>
      <Stack
        direction="row"
        spacing={1.5}
        sx={{
          alignItems: 'center',
          mt: 3,
          color: 'text.secondary',
        }}
      >
        <Box sx={{ display: 'flex', fontSize: 20 }}>
          <IoBatteryFullOutline />
        </Box>
        <Typography variant="caption">ご要望はお気軽にお問い合わせください。</Typography>
      </Stack>
    </Box>
  )
}

type SpecRowProps = { label: string; value: React.ReactNode }
const SpecRow = ({ label, value }: SpecRowProps) => {
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.5, sm: 2 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', minWidth: { sm: 120 }, pt: { sm: '2px' } }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 15, color: 'text.primary', fontWeight: 500 }}>{value}</Typography>
    </Stack>
  )
}

const StorySection = () => {
  return (
    <Box>
      <SectionHeading eyebrow="STORY" title="ワナップのストーリー" />
      <Stack
        spacing={3}
        sx={{
          mt: 4,
        }}
      >
        <StoryCard
          title="ワナップを作ろうと思った理由"
          body="ワナップは会社代表が猟師として活動する中で生まれました。会社員として仕事をしながら罠猟を行っていく中で、見回りが非常に大変だということに気付き、IoT罠の購入を検討。しかし既存のサービスは高額で使いづらく、「それなら自分で作ってしまえ」と開発が始まりました。エンジニアとしてのスキルと、猟師としてのニーズを融合させた、安くて使いやすいIoT罠管理サービスです。"
        />
        <StoryCard
          title="ワナップが目指すこと"
          body="獣害による農業被害額は今や年156億円(2022年調べ)。一方、領域を分けるために尽力している猟師は年々高齢化しています。毎日の見回りは高齢猟師にとって大きな負担であり、現役世代にとっても初心者ハンターの挑戦を阻む壁になっています。安価で使いやすいワナップが行政・ベテラン・初心者の手に行き渡ることで、農業・森林被害の削減に貢献し、最終的には鳥獣と人間との真の共存を目指します。"
        />
      </Stack>
    </Box>
  )
}

const StoryCard = ({ title, body }: { title: string; body: string }) => {
  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 3, md: 4 },
        borderRadius: 3,
        border: '1px solid',
        borderLeftWidth: 4,
        borderLeftColor: 'primary.main',
        borderColor: 'divider',
        backgroundColor: 'background.paper',
      }}
    >
      <Typography variant="h6" sx={{ mb: 1.5 }}>
        {title}
      </Typography>
      <Typography sx={{ fontSize: { xs: 14, md: 15 }, lineHeight: 1.9, color: 'text.secondary' }}>{body}</Typography>
    </Paper>
  )
}

const PressSection = () => {
  return (
    <Box>
      <SectionHeading eyebrow="PRESS" title="ご紹介いただきました" />
      <Stack
        spacing={3}
        sx={{
          mt: 4,
        }}
      >
        <PressCard
          title="罠の作動をアプリで通知「ワナップ」で見回り効率化"
          credit="狩猟生活 (山と渓谷社さま)"
          href="https://note.com/syuryo_seikatsu/n/n96b81196bc98"
        >
          <Box
            component="img"
            src="https://assets.st-note.com/production/uploads/images/171136485/rectangle_large_type_2_b0b99395c1cbbf90fcb433913a556ba2.png"
            alt="狩猟生活掲載記事"
            sx={{ width: '100%', display: 'block', borderRadius: 2 }}
          />
        </PressCard>
        <PressCard credit="lifeさま">
          <YoutubeEmbed src="https://www.youtube.com/embed/bOzOi1Bky1w?si=CutIwQlYRyaJa3uq" />
        </PressCard>
        <PressCard credit="わな猟師せきはんさま">
          <YoutubeEmbed src="https://www.youtube.com/embed/jWcNlN7g7VA?si=QwvWsm5vx3_SW17k" />
        </PressCard>
      </Stack>
    </Box>
  )
}

type PressCardProps = { title?: string; credit: string; href?: string; children: React.ReactNode }
const PressCard = ({ title, credit, href, children }: PressCardProps) => {
  const inner = (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2.5, md: 3 },
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        transition: 'box-shadow .2s, transform .2s',
        '&:hover': { boxShadow: 6, transform: 'translateY(-2px)' },
      }}
    >
      <Stack spacing={2}>
        {title && (
          <Typography variant="h6" sx={{ fontSize: { xs: 16, md: 18 } }}>
            {title}
          </Typography>
        )}
        {children}
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {credit}
        </Typography>
      </Stack>
    </Paper>
  )
  return href ? (
    <Link to={href} target="_blank" style={{ textDecoration: 'none' }}>
      {inner}
    </Link>
  ) : (
    inner
  )
}

const YoutubeEmbed = ({ src }: { src: string }) => {
  return (
    <Box sx={{ position: 'relative', paddingTop: '56.25%', borderRadius: 2, overflow: 'hidden' }}>
      <iframe
        src={src}
        title="YouTube video player"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        referrerPolicy="strict-origin-when-cross-origin"
        allowFullScreen
        loading="lazy"
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
      />
    </Box>
  )
}

const FinalCTA = () => {
  return (
    <Box
      sx={{
        position: 'relative',
        borderRadius: { xs: 3, md: 4 },
        overflow: 'hidden',
        backgroundImage: `linear-gradient(135deg, rgba(255,153,10,0.95) 0%, rgba(255,171,38,0.9) 100%), url(/kukuri_wana.jpg)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        color: '#fff',
        px: { xs: 3, sm: 5, md: 8 },
        py: { xs: 6, md: 9 },
        textAlign: 'center',
      }}
    >
      <Typography component="h2" sx={{ fontWeight: 800, fontSize: { xs: 24, sm: 28, md: 34 }, letterSpacing: '-0.01em', lineHeight: 1.4 }}>
        さあ、{serviceName}で快適な狩猟を。
      </Typography>
      <Typography sx={{ mt: 2, fontSize: { xs: 14, md: 16 }, opacity: 0.95 }}>ご利用申請は数分で完了します。お気軽にお問い合わせください。</Typography>
      <Box
        sx={{
          mt: 4,
        }}
      >
        <Button
          component={Link}
          to="/inquiry/register"
          variant="contained"
          size="large"
          endIcon={<IoArrowForward />}
          sx={{
            borderRadius: 999,
            px: 5,
            py: 1.5,
            fontWeight: 700,
            backgroundColor: '#fff',
            color: 'primary.dark',
            boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
            '&:hover': { backgroundColor: '#fff', boxShadow: '0 10px 26px rgba(0,0,0,0.2)' },
          }}
        >
          ご利用申請
        </Button>
      </Box>
    </Box>
  )
}

type SectionHeadingProps = { eyebrow: string; title: string }
const SectionHeading = ({ eyebrow, title }: SectionHeadingProps) => {
  return (
    <Box>
      <Stack
        direction="row"
        spacing={1.5}
        sx={{
          alignItems: 'center',
        }}
      >
        <Box sx={{ width: 32, height: 2, backgroundColor: 'primary.main', borderRadius: 1 }} />
        <Typography variant="overline" sx={{ color: 'primary.dark' }}>
          {eyebrow}
        </Typography>
      </Stack>
      <Typography component="h2" sx={{ mt: 1, fontWeight: 800, fontSize: { xs: 26, md: 32 }, letterSpacing: '-0.01em' }}>
        {title}
      </Typography>
    </Box>
  )
}

const NewsSection = () => {
  const { listNews } = useNews()
  const MAX_DISPLAY = 5

  if (listNews.isLoading || !listNews.data || listNews.data.length === 0) {
    return null
  }

  const displayNews = listNews.data.slice(0, MAX_DISPLAY)
  const hasMore = listNews.data.length > MAX_DISPLAY

  return (
    <Box>
      <SectionHeading eyebrow="NEWS" title="ニュース" />
      <Stack
        spacing={1.5}
        sx={{
          mt: 4,
        }}
      >
        {displayNews.map((news) => (
          <NewsItem key={news.id} news={news} />
        ))}
      </Stack>
      {hasMore && (
        <Box
          sx={{
            mt: 2,
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <Button component={Link} to="/news" variant="text" endIcon={<IoArrowForward />} sx={{ fontWeight: 700, color: 'primary.dark' }}>
            もっと見る
          </Button>
        </Box>
      )}
    </Box>
  )
}

const NewsItem = ({ news }: { news: News }) => {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        transition: 'box-shadow .2s, border-color .2s',
        '&:hover': { boxShadow: 4, borderColor: 'primary.main' },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Link to={`/news/${news.id}`} style={{ textDecoration: 'none', flex: 1 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 0.5, sm: 2 }}
            sx={{
              alignItems: { sm: 'center' },
            }}
          >
            <Typography variant="caption" sx={{ color: 'text.secondary', whiteSpace: 'nowrap', minWidth: { sm: 90 } }}>
              {formatTimestampToDate(news.createdAt)}
            </Typography>
            <Typography
              variant="subtitle1"
              sx={{
                color: 'text.primary',
                fontWeight: 600,
              }}
            >
              {news.title}
            </Typography>
          </Stack>
        </Link>
      </Box>
    </Paper>
  )
}
